import React, { useState, useEffect } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Typography, Avatar, IconButton, Grid,
    TextField, MenuItem, Switch, Button, Alert, CircularProgress, Stack,
} from "@mui/material";
import EventNoteIcon from "@mui/icons-material/EventNote";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";

import { LOYALTY_API_BASE, SECTION_COLORS } from "../../constants";
import { loyaltyFetch } from "../../hooks/loyaltyFetch";

const ORIGINS = ["INTERNAL", "EXTERNAL"];

const EditTransactionTypeDialog = ({ open, transactionType, onClose, onSaved }) => {
    const [form, setForm] = useState({ key: "", origin: "INTERNAL", name: "", description: "", payload_fields: [], active: true, brand: "" });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (transactionType) {
            const fields = [];
            if (transactionType.payload_schema) {
                Object.entries(transactionType.payload_schema).forEach(([key, val]) => {
                    fields.push({
                        id: Math.random().toString(36).substr(2, 9),
                        key,
                        type: val.type || "string",
                        description: val.description || ""
                    });
                });
            }

            setForm({
                key: transactionType.key || "",
                origin: transactionType.origin || "INTERNAL",
                name: transactionType.name || "",
                description: transactionType.description || "",
                payload_fields: fields,
                active: transactionType.active ?? true,
                brand: transactionType.brand || "",
            });
            setError(null);
        }
    }, [transactionType]);

    const handle = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));
    const isValid = form.origin.trim() && form.name.trim();

    const handleAddField = () => {
        setForm(p => ({
            ...p,
            payload_fields: [...p.payload_fields, { id: Date.now(), key: "", type: "string", description: "" }]
        }));
    };

    const handleUpdateField = (id, field, value) => {
        setForm(p => ({
            ...p,
            payload_fields: p.payload_fields.map(f => f.id === id ? { ...f, [field]: value } : f)
        }));
    };

    const handleRemoveField = (id) => {
        setForm(p => ({
            ...p,
            payload_fields: p.payload_fields.filter(f => f.id !== id)
        }));
    };

    const handleSubmit = async () => {
        if (!isValid || !transactionType) return;
        setSubmitting(true);
        setError(null);
        try {
            // Convert list to object
            const payloadSchema = {};
            form.payload_fields.forEach(f => {
                if (f.key.trim()) {
                    payloadSchema[f.key.trim()] = {
                        type: f.type,
                        description: f.description.trim() || undefined
                    };
                }
            });

            const body = {
                origin: form.origin.trim(),
                name: form.name.trim(),
                description: form.description.trim() || null,
                payload_schema: Object.keys(payloadSchema).length > 0 ? payloadSchema : null,
                active: form.active,
                brand: form.brand || null,
            };
            const res = await loyaltyFetch(`${LOYALTY_API_BASE}/admin/transaction-types/${transactionType.id}`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    "X-Brand": form.brand || transactionType.brand
                },
                body: JSON.stringify(body),
            });
            if (!res.ok) { const e = await res.json().catch(() => null); throw new Error(e?.detail || `Erreur HTTP ${res.status}`); }
            onSaved(); onClose();
        } catch (err) { setError(err.message); } finally { setSubmitting(false); }
    };

    if (!transactionType) return null;
    const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: 2 } };
    const monoFieldSx = { ...fieldSx, "& textarea": { fontFamily: "monospace", fontSize: 12 } };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ borderBottom: "2px solid #f1f3f5", px: 3, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ width: 32, height: 32, background: SECTION_COLORS.transactionTypes.gradient }}>
                        <EventNoteIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                    <Box>
                        <Typography variant="h6" fontFamily="Montserrat, sans-serif" fontWeight={700} color="#1a1a2e" fontSize={16}>
                            Modifier le Type de Transaction
                        </Typography>
                        <Typography fontSize={11} color="text.secondary">{transactionType.key}</Typography>
                    </Box>
                </Box>
                <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: "24px !important", px: 3, pb: 2 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={5}>
                        <TextField fullWidth required select label="Origine" value={form.origin}
                            onChange={handle("origin")} size="small" sx={fieldSx}>
                            {ORIGINS.map((o) => (
                                <MenuItem key={o} value={o}>
                                    {o === "INTERNAL" ? "Internal" : "External"}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField fullWidth required label="Nom affiché" value={form.name}
                            onChange={handle("name")} size="small" sx={fieldSx} />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField fullWidth label="Description" value={form.description}
                            onChange={handle("description")} size="small" multiline rows={2} sx={fieldSx} />
                    </Grid>

                    {/* payload_schema user friendly */}
                    <Grid item xs={12}>
                        <Box mt={1} p={2} sx={{ borderRadius: 2, bgcolor: "#f8f9fa", border: "1px dashed #ced4da" }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                                <Typography variant="subtitle2" fontWeight={700} color="#495057">
                                    Structure des données (Payload)
                                </Typography>
                                <Button size="small" startIcon={<AddCircleOutlineIcon />} onClick={handleAddField}
                                    sx={{ color: SECTION_COLORS.transactionTypes.main, textTransform: "none", fontWeight: 600 }}>
                                    Ajouter un champ
                                </Button>
                            </Box>

                            {form.payload_fields?.length === 0 ? (
                                <Typography fontSize={12} color="text.secondary" align="center" py={2}>
                                    Aucun champ personnalisé défini. Cliquez sur "Ajouter" pour commencer.
                                </Typography>
                            ) : (
                                <Stack spacing={1.5}>
                                    {(form.payload_fields || []).map((f) => (
                                        <Box key={f.id} display="flex" gap={1} alignItems="flex-start">
                                            <TextField size="small" label="Nom du champ" value={f.key}
                                                onChange={(e) => handleUpdateField(f.id, "key", e.target.value)}
                                                placeholder="ex: order_id" sx={{ flex: 1, ...fieldSx }} />

                                            <TextField size="small" select label="Type" value={f.type}
                                                onChange={(e) => handleUpdateField(f.id, "type", e.target.value)}
                                                sx={{ width: 110, ...fieldSx }}>
                                                <MenuItem value="string">Texte</MenuItem>
                                                <MenuItem value="number">Nombre</MenuItem>
                                                <MenuItem value="boolean">Booléen</MenuItem>
                                            </TextField>

                                            <TextField size="small" label="Description" value={f.description}
                                                onChange={(e) => handleUpdateField(f.id, "description", e.target.value)}
                                                placeholder="Facultatif" sx={{ flex: 1.5, ...fieldSx }} />

                                            <IconButton size="small" color="error" onClick={() => handleRemoveField(f.id)} sx={{ mt: 0.5 }}>
                                                <DeleteOutlineIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    ))}
                                </Stack>
                            )}
                        </Box>
                    </Grid>

                    <Grid item xs={12}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" px={1.5} py={1}
                            sx={{ borderRadius: 2, bgcolor: "#fafbfd", border: "1px solid #e8ecf1" }}>
                            <Box>
                                <Typography fontSize={13} fontWeight={600}>Activer</Typography>
                                <Typography fontSize={11} color="text.secondary">Type disponible pour les règles</Typography>
                            </Box>
                            <Switch checked={form.active} onChange={() => setForm((p) => ({ ...p, active: !p.active }))}
                                sx={{
                                    "& .MuiSwitch-switchBase.Mui-checked": { color: SECTION_COLORS.transactionTypes.main },
                                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: SECTION_COLORS.transactionTypes.main }
                                }} />
                        </Box>
                    </Grid>
                </Grid>
                {error && <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>{error}</Alert>}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e0e0e0", gap: 1 }}>
                <Button onClick={onClose} sx={{ color: "text.secondary", textTransform: "none" }}>Annuler</Button>
                <Button variant="contained" onClick={handleSubmit} disabled={!isValid || submitting}
                    startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    sx={{ background: SECTION_COLORS.transactionTypes.gradient, textTransform: "none", borderRadius: 2, fontWeight: 600, px: 3 }}>
                    {submitting ? "Enregistrement..." : "Enregistrer"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditTransactionTypeDialog;
